import React, { useMemo, useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Space, Button, Alert, message } from "@mimber/ui-web"
import { Form, InputField, SelectField, CheckboxField } from "@mimber/forms"
import {
  useCreateMember,
  useCreateInvitation,
  useMembershipTiers,
} from "@mimber/client/mimber-organization"
import { CreateMemberConflictCode } from "@mimber/schema"
import type { ApiError } from "@mimber/schema"
import type { CreateMemberFormProps } from "./CreateMemberForm.types"
import {
  createMemberSchema,
  type CreateMemberFormData,
} from "./CreateMemberForm.schema"

export const CreateMemberForm: React.FunctionComponent<
  CreateMemberFormProps
> = ({ onSuccess, onCancel }) => {
  const [conflictState, setConflictState] = useState<{
    code: string
    email: string
    name: string
  } | null>(null)

  const form = useForm<CreateMemberFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(createMemberSchema) as any,
    mode: "onBlur",
    defaultValues: {
      email: "",
      firstName: "",
      lastName: "",
      tierId: undefined,
      sendInvitation: true,
    },
  })

  const {
    reset,
  } = form

  const { data: tiersResponse, isLoading: tiersLoading } = useMembershipTiers({
    limit: 100,
  })

  const tierOptions = useMemo(() => {
    if (!tiersResponse?.data) return []
    return tiersResponse.data
      .filter((tier) => tier.isActive)
      .map((tier) => ({
        label: tier.name,
        value: tier.uuid,
      }))
  }, [tiersResponse])

  const createMemberMutation = useCreateMember({
    onSuccess: (response) => {
      message.success("Member added successfully")
      reset()
      setConflictState(null)
      onSuccess?.(response.data)
    },
    onError: (error: ApiError) => {
      const details = (error as any)?.response?.data?.meta?.error?.details
      const code = details?.code

      if (code === CreateMemberConflictCode.USER_HAS_ACCOUNT) {
        const values = form.getValues()
        setConflictState({
          code: CreateMemberConflictCode.USER_HAS_ACCOUNT,
          email: values.email,
          name: `${values.firstName} ${values.lastName}`,
        })
      } else if (code === CreateMemberConflictCode.ALREADY_MEMBER) {
        setConflictState({
          code: CreateMemberConflictCode.ALREADY_MEMBER,
          email: form.getValues().email,
          name: "",
        })
      } else {
        message.error(`Failed to add member: ${error.message}`)
      }
    },
  })

  const createInvitationMutation = useCreateInvitation({
    onSuccess: () => {
      message.success("Invitation sent successfully")
      reset()
      setConflictState(null)
      onCancel?.()
    },
    onError: (error) => {
      message.error(`Failed to send invitation: ${error.message}`)
    },
  })

  const handleSendInvitation = () => {
    if (!conflictState) return
    createInvitationMutation.mutate({
      email: conflictState.email,
      name: conflictState.name,
    })
  }

  const onSubmit = (data: CreateMemberFormData) => {
    setConflictState(null)
    createMemberMutation.mutate(data)
  }

  const isPending =
    createMemberMutation.isPending || createInvitationMutation.isPending

  if (conflictState?.code === CreateMemberConflictCode.USER_HAS_ACCOUNT) {
    return (
      <div>
        <Alert
          type="warning"
          showIcon
          message="This person already has a Mimber account"
          description="Would you like to send them an invitation to join your organization instead?"
          style={{ marginBottom: 16 }}
        />
        <Space style={{ width: "100%", justifyContent: "flex-end" }}>
          <Button
            onClick={() => {
              setConflictState(null)
            }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleSendInvitation}
            loading={createInvitationMutation.isPending}
          >
            Send Invitation
          </Button>
        </Space>
      </div>
    )
  }

  if (conflictState?.code === CreateMemberConflictCode.ALREADY_MEMBER) {
    return (
      <div>
        <Alert
          type="error"
          showIcon
          message="Already a member"
          description="This person is already a member of your organization."
          style={{ marginBottom: 16 }}
        />
        <Space style={{ width: "100%", justifyContent: "flex-end" }}>
          <Button
            onClick={() => {
              setConflictState(null)
            }}
          >
            Go Back
          </Button>
        </Space>
      </div>
    )
  }

  return (
    <Form
      form={form}
      onSubmit={onSubmit}
      disabled={isPending}
      layout="vertical"
    >
      <InputField
        name="email"
        label="Email Address"
        type="email"
        placeholder="Enter member's email address"
        required
      />

      <InputField
        name="firstName"
        label="First Name"
        placeholder="Enter member's first name"
        required
      />

      <InputField
        name="lastName"
        label="Last Name"
        placeholder="Enter member's last name"
        required
      />

      <SelectField
        name="tierId"
        label="Membership Tier"
        placeholder="Select a tier (optional)"
        options={tierOptions}
        loading={tiersLoading}
        allowClear
        showSearch
        filterOption={(input, option) =>
          String(option?.label ?? "")
            .toLowerCase()
            .includes(input.toLowerCase())
        }
      />

      <CheckboxField name="sendInvitation">
        Send welcome email to the member
      </CheckboxField>

      <Space
        style={{ width: "100%", justifyContent: "flex-end", marginTop: 24 }}
      >
        <Button
          type="primary"
          htmlType="submit"
          loading={createMemberMutation.isPending}

        >
          Add Member
        </Button>
      </Space>
    </Form>
  )
}
